import type {
    ContextMenuCheckboxItemProps,
    ContextMenuItemProps,
    ContextMenuProps,
    ContextMenuRadioGroupProps,
    ContextMenuRadioItemProps,
    ContextMenuSubContentProps,
    ContextMenuSubProps,
    ContextMenuSubTriggerProps,
} from '@radix-ui/react-context-menu';
import {
    CheckboxItem,
    Content,
    Group,
    Item,
    ItemIndicator,
    Label,
    Portal,
    RadioGroup,
    RadioItem,
    Root,
    Separator,
    Sub,
    SubContent,
    SubTrigger,
    Trigger,
} from '@radix-ui/react-context-menu';
import is from '@sindresorhus/is';
import clsx from 'clsx';
import type { ComponentPropsWithoutRef, FunctionComponent, PropsWithChildren, ReactNode } from 'react';

import { useColorSchemeScope } from '../color-scheme/ColorSchemeScope';
import { useRestoreMenuFocusWithoutScroll } from '../focus/restore-menu-trigger-focus';
import { floatingSurfaceClassNames, floatingSurfaceItemClassNames } from '../surface/surfaceClasses';
import { Tooltip } from '../tooltip/Tooltip';

// biome-ignore lint/complexity/noBannedTypes: suppressed due to migration
export type ContextMenuSeparatorProps = {};

export type ContextMenuLabelProps = {
    readonly children: ReactNode;
};

export type ContextMenuGroupProps = {
    readonly children: ReactNode;
};

const contentClasses = clsx(
    floatingSurfaceClassNames.tone.gray,
    'font-sans rounded-xl min-w-[220px] overflow-hidden p-xs z-[1000]',
);

const baseItemClasses = clsx(
    'flex items-center rounded-sm text-text-form cursor-pointer',
    'text-body-xxs font-normal',
    'py-s px-m relative select-none',
    'data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none',
);

const highlightedClasses = floatingSurfaceItemClassNames.highlighted;

const checkedClasses = floatingSurfaceItemClassNames.checked;

const disabledGroupClasses =
    'data-[disabled]:text-text-quiet data-[disabled]:cursor-default data-[disabled]:pointer-events-none';

export const ContextMenu: FunctionComponent<ContextMenuProps> = ({ children, onOpenChange, ...props }) => {
    return (
        <Root onOpenChange={onOpenChange} {...props}>
            {children}
        </Root>
    );
};

export const ContextMenuTrigger: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <Trigger asChild={true}>{children}</Trigger>;
};

export const ContextMenuItem: FunctionComponent<ContextMenuItemProps & { readonly tooltipContent?: string }> = ({
    children,
    className,
    disabled,
    onSelect,
    tooltipContent,
    ...props
}) => {
    const menuItem = (
        <Item
            className={clsx(
                baseItemClasses,
                'gap-xs',
                '[&_svg]:text-text-content-faint',
                highlightedClasses,
                'data-[disabled]:[&_svg]:text-text-quiet',
                className,
            )}
            disabled={disabled}
            onSelect={onSelect}
            {...props}
        >
            {children}
        </Item>
    );

    if (disabled === true && is.nonEmptyString(tooltipContent)) {
        return (
            <Tooltip>
                <Tooltip.Trigger asChild={true}>
                    <div style={{ cursor: 'not-allowed', display: 'flex' }}>{menuItem}</div>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                    <Tooltip.Content side="right" sideOffset={5}>
                        {tooltipContent}
                    </Tooltip.Content>
                </Tooltip.Portal>
            </Tooltip>
        );
    }

    return menuItem;
};

export const ContextMenuCheckboxItem: FunctionComponent<ContextMenuCheckboxItemProps> = ({
    checked,
    children,
    className,
    disabled,
    onCheckedChange,
    ...props
}) => {
    return (
        <CheckboxItem
            checked={checked}
            className={clsx(
                baseItemClasses,
                floatingSurfaceItemClassNames.hover,
                highlightedClasses,
                checkedClasses,
                className,
            )}
            disabled={disabled}
            onCheckedChange={onCheckedChange}
            {...props}
        >
            {children}
        </CheckboxItem>
    );
};

export const ContextMenuSeparator: FunctionComponent<ContextMenuSeparatorProps> = ({ ...props }) => {
    return <Separator className="bg-border-subtle h-px m-xs" {...props} />;
};

export const ContextMenuLabel: FunctionComponent<ContextMenuLabelProps> = ({ children, ...props }) => {
    return (
        <Label className="text-text-content-subtle text-xxxs font-semibold uppercase tracking-[0.08em] p-s" {...props}>
            {children}
        </Label>
    );
};

export const ContextMenuGroup: FunctionComponent<ContextMenuGroupProps> = ({ children, ...props }) => {
    return (
        <Group className={disabledGroupClasses} {...props}>
            {children}
        </Group>
    );
};

export const ContextMenuSub: FunctionComponent<ContextMenuSubProps> = ({ children, onOpenChange, open, ...props }) => {
    return (
        <Sub open={open} onOpenChange={onOpenChange} {...props}>
            {children}
        </Sub>
    );
};

export const ContextMenuSubTrigger: FunctionComponent<ContextMenuSubTriggerProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <SubTrigger
            className={clsx(
                baseItemClasses,
                'justify-between',
                '[&_svg]:text-text-content-faint',
                floatingSurfaceItemClassNames.hover,
                highlightedClasses,
                'data-[state=open]:bg-surface-subtle',
                'data-[disabled]:[&_svg]:text-text-quiet',
                className,
            )}
            {...props}
        >
            {children}
        </SubTrigger>
    );
};

export const ContextMenuSubContent: FunctionComponent<ContextMenuSubContentProps> = ({
    alignOffset,
    children,
    className,
    sideOffset,
    ...props
}) => {
    const colorScheme = useColorSchemeScope();

    return (
        <Portal>
            <SubContent
                alignOffset={alignOffset}
                className={clsx(contentClasses, className)}
                data-color-scheme={colorScheme}
                sideOffset={sideOffset}
                {...props}
            >
                {children}
            </SubContent>
        </Portal>
    );
};

type ContextMenuContentProps = PropsWithChildren<{
    readonly className?: string;
    readonly ['data-comment-button']?: boolean;
    readonly onCloseAutoFocus?: (event: Event) => void;
    readonly onInteractOutside?: ComponentPropsWithoutRef<typeof Content>['onInteractOutside'];
    readonly onOpenAutoFocus?: (event: Event) => void;
}>;

export const ContextMenuContent: FunctionComponent<ContextMenuContentProps> = ({
    children,
    className,
    onCloseAutoFocus,
    onInteractOutside,
    onOpenAutoFocus,
    ...props
}) => {
    const colorScheme = useColorSchemeScope();
    const focusHandlers = useRestoreMenuFocusWithoutScroll({ onCloseAutoFocus, onInteractOutside, onOpenAutoFocus });

    return (
        <Portal>
            <Content
                className={clsx(contentClasses, className)}
                data-color-scheme={colorScheme}
                {...focusHandlers}
                {...props}
            >
                {children}
            </Content>
        </Portal>
    );
};

export const ContextMenuItemIndicator: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return (
        <ItemIndicator
            className="inline-flex shrink-0 items-center justify-center text-text-form ml-s"
            data-testid="menu-item-indicator"
        >
            {children}
        </ItemIndicator>
    );
};

export const ContextMenuRadioGroup: FunctionComponent<ContextMenuRadioGroupProps> = ({ children, ...props }) => {
    return (
        <RadioGroup className={disabledGroupClasses} {...props}>
            {children}
        </RadioGroup>
    );
};

export const ContextMenuRadioItem: FunctionComponent<ContextMenuRadioItemProps> = ({
    children,
    className,
    ...props
}) => {
    return (
        <RadioItem
            className={clsx(
                baseItemClasses,
                floatingSurfaceItemClassNames.hover,
                highlightedClasses,
                checkedClasses,
                className,
            )}
            {...props}
        >
            {children}
        </RadioItem>
    );
};

export const ContextMenuSubTriggerContent: FunctionComponent<PropsWithChildren> = ({ children }) => {
    return <div className="flex items-center gap-xs">{children}</div>;
};
